import React, { useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import InviteLinkShare from '@/components/InviteLinkShare';
import { getDateLocale } from '@/utils/dateLocale';
import { INVITE_LINK_STATES } from '@/constants';
import type { InviteLink } from '@/types/inviteLink';
import styles from './styles.module.scss';

interface InviteLinkListProps {
  links: InviteLink[];
  expandedId: number | null;
  onToggleExpand: (linkId: number) => void;
  onRevoke: (linkId: number) => void;
  revokingId: number | null;
  onDelete: (linkId: number) => void;
  deletingId: number | null;
}

const formatDate = (iso: string) => format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: getDateLocale() });

type PendingConfirm = 'revoke' | 'delete' | null;

interface RowProps {
  link: InviteLink;
  isExpanded: boolean;
  isRevoking: boolean;
  isDeleting: boolean;
  onToggleExpand: () => void;
  onRevoke: () => void;
  onDelete: () => void;
}

interface ConfirmProps {
  text: string;
  confirmLabel: string;
  cancelLabel: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmTestId: string;
  cancelTestId: string;
}

const InlineConfirm: React.FC<ConfirmProps> = ({
  text,
  confirmLabel,
  cancelLabel,
  isPending,
  onConfirm,
  onCancel,
  confirmTestId,
  cancelTestId,
}) => (
  <div className={styles.confirm} role="group" aria-label={text}>
    <span className={styles.confirmText}>{text}</span>
    <button
      type="button"
      className="btn btn--sm decline"
      disabled={isPending}
      onClick={onConfirm}
      data-testid={confirmTestId}
    >
      {confirmLabel}
    </button>
    <button
      type="button"
      className="btn btn--sm btn--outlined"
      disabled={isPending}
      onClick={onCancel}
      data-testid={cancelTestId}
    >
      {cancelLabel}
    </button>
  </div>
);

const InviteLinkRow: React.FC<RowProps> = ({
  link,
  isExpanded,
  isRevoking,
  isDeleting,
  onToggleExpand,
  onRevoke,
  onDelete,
}) => {
  const { t } = useTranslation(['tournament', 'common']);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);
  const isActive = link.state === INVITE_LINK_STATES.ACTIVE;
  const cancelConfirm = () => setPendingConfirm(null);

  const renderActions = () => {
    if (pendingConfirm === 'revoke') {
      return (
        <InlineConfirm
          text={t('inviteLink.revokeConfirm')}
          confirmLabel={t('inviteLink.revokeYes')}
          cancelLabel={t('common:cancel')}
          isPending={isRevoking}
          onConfirm={onRevoke}
          onCancel={cancelConfirm}
          confirmTestId={`invite-link-revoke-confirm-${link.id}`}
          cancelTestId={`invite-link-revoke-cancel-${link.id}`}
        />
      );
    }
    if (pendingConfirm === 'delete') {
      return (
        <InlineConfirm
          text={t('inviteLink.deleteConfirm')}
          confirmLabel={t('inviteLink.deleteYes')}
          cancelLabel={t('common:cancel')}
          isPending={isDeleting}
          onConfirm={onDelete}
          onCancel={cancelConfirm}
          confirmTestId={`invite-link-delete-confirm-${link.id}`}
          cancelTestId={`invite-link-delete-cancel-${link.id}`}
        />
      );
    }
    return (
      <>
        {isActive && link.token && (
          <button
            type="button"
            className="btn btn--sm btn--outlined"
            aria-expanded={isExpanded}
            onClick={onToggleExpand}
            data-testid={`invite-link-toggle-qr-${link.id}`}
          >
            {isExpanded ? t('inviteLink.hideQr') : t('inviteLink.showQr')}
          </button>
        )}
        {isActive && (
          <button
            type="button"
            className="btn btn--sm btn--outlined"
            onClick={() => setPendingConfirm('revoke')}
            data-testid={`invite-link-revoke-${link.id}`}
          >
            {t('inviteLink.revoke')}
          </button>
        )}
        <button
          type="button"
          className={`btn btn--sm btn--outlined ${styles.iconBtn}`}
          aria-label={t('inviteLink.delete')}
          title={t('inviteLink.delete')}
          onClick={() => setPendingConfirm('delete')}
          data-testid={`invite-link-delete-${link.id}`}
        >
          <i className="icon-trash" aria-hidden="true" />
        </button>
      </>
    );
  };

  return (
    <li className={styles.row} data-testid={`invite-link-row-${link.id}`} data-state={link.state}>
      <div className={styles.meta}>
        <span
          className={`${styles.badge} ${isActive ? styles.badgeActive : ''}`}
          data-testid={`invite-link-state-${link.id}`}
        >
          {t(`inviteLink.state.${link.state}`)}
        </span>
        <span className={styles.uses} data-testid={`invite-link-uses-${link.id}`}>
          {t('inviteLink.uses', { used: link.usedCount, max: link.maxUses })}
        </span>
        <span className={styles.expiry}>
          {t(isActive ? 'inviteLink.expiresAt' : 'inviteLink.validUntil', { date: formatDate(link.expiresAt) })}
        </span>
      </div>

      {isActive && link.token === null && (
        <p className={styles.hint} data-testid={`invite-link-regenerate-hint-${link.id}`}>
          {t('inviteLink.regenerateHint')}
        </p>
      )}

      <div className={styles.actions}>{renderActions()}</div>

      {isActive && isExpanded && link.token && (
        <InviteLinkShare token={link.token} linkId={link.id} />
      )}
    </li>
  );
};

export const InviteLinkList: React.FC<InviteLinkListProps> = ({
  links,
  expandedId,
  onToggleExpand,
  onRevoke,
  revokingId,
  onDelete,
  deletingId,
}) => (
  <ul className={styles.list} data-testid="invite-link-list">
    {links.map((link) => (
      <InviteLinkRow
        key={link.id}
        link={link}
        isExpanded={expandedId === link.id}
        isRevoking={revokingId === link.id}
        isDeleting={deletingId === link.id}
        onToggleExpand={() => onToggleExpand(link.id)}
        onRevoke={() => onRevoke(link.id)}
        onDelete={() => onDelete(link.id)}
      />
    ))}
  </ul>
);

export default InviteLinkList;
